'use client';

import { publicEnv } from '@/lib/config/public-env';
import { useFeatureFlags } from '@/@feature-flags';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../constants';
import { studioAIService } from '../services';
import type { PlanTemplateResponse } from '../types';

export interface UsePlanTemplatesOptions {
    /**
     * When false the query is skipped so we don't hit the endpoint from
     * screens that hide the templates UI (freemium, masqueraded sessions,
     * planner-mode flag off, etc.).
     */
    enabled?: boolean;
}

const showAllTemplates = publicEnv('NEXT_PUBLIC_SHOW_ALL_TEMPLATES') === 'true';

export function usePlanTemplates({ enabled = true }: UsePlanTemplatesOptions = {}) {
    const { hasFlag } = useFeatureFlags();

    const { data, isLoading, error } = useQuery<PlanTemplateResponse[]>({
        queryKey: showAllTemplates
            ? [...QUERY_KEYS.PLAN_TEMPLATES, 'all']
            : QUERY_KEYS.PLAN_TEMPLATES,
        queryFn: showAllTemplates
            ? () =>
                  studioAIService
                      .getAdminPlanTemplates({ visibleOnly: false, limit: 100 })
                      .then((res) => res.data)
            : () => studioAIService.getPlanTemplates(),
        staleTime: 1000 * 60 * 10,
        enabled,
    });

    // Filter out templates that require a feature flag the current user doesn't have.
    // If featureFlagKey is undefined (backend doesn't support it yet), the template is shown.
    const filtered = (data ?? []).filter((t) => !t.featureFlagKey || hasFlag(t.featureFlagKey));

    return { data: filtered, isLoading, error };
}
